/**
 * Turns the `link` a notification was stored with into a route this app has.
 *
 * Producers wrote several links to routes that do not exist here, and the
 * router's catch-all redirects an unknown path to "/", so "View" silently
 * dropped the user on All Companies. Stored rows keep their old links, so the
 * mapping lives on the read side. Returns undefined when there is no page to
 * open, which hides the View button.
 */
const LEGACY_RECONCILIATION = /^\/reconciliation\/([^/?#]+)\/?$/;
const LEGACY_CONNECTIONS = /^\/company\/(\d+)\/connections\/?$/;
const WORKFLOW_RUN = /^\/workflow\/runs\/[^/]+\/?$/;

function toInAppPath(link: string): string {
	if (!/^https?:\/\//i.test(link)) return link;
	let url: URL;
	try {
		url = new URL(link);
	} catch {
		return link;
	}
	// The bank reconnect link is built from FRONTEND_URL, which is not always
	// this origin, so match it by path wherever it points.
	if (
		url.origin === window.location.origin ||
		url.pathname === "/settings/banks"
	) {
		return `${url.pathname}${url.search}${url.hash}`;
	}
	return link;
}

export function resolveNotificationLink(
	link: string | undefined,
	companyId: number | undefined,
): string | undefined {
	if (!link) return undefined;
	const path = toInAppPath(link);

	const rec = LEGACY_RECONCILIATION.exec(path);
	if (rec) {
		return companyId != null
			? `/company/${companyId}/reconciles/results?reconciliationId=${rec[1]}`
			: undefined;
	}

	const connections = LEGACY_CONNECTIONS.exec(path);
	if (connections) {
		return `/company/${connections[1]}/settings/accounting`;
	}

	if (path === "/account/subscription") return "/account/billing";

	if (path === "/settings/banks") {
		return companyId != null
			? `/company/${companyId}/settings/banks`
			: undefined;
	}

	if (WORKFLOW_RUN.test(path)) return undefined;

	return path;
}
